import React from "react";
import { Reveal, RevealImage } from "@/lib/motion";
import { Page, PageHero, Overline, CTABand } from "@/components/shared";
import { useContent, useFeaturedDoctor } from "@/lib/ContentProvider";

export default function About() {
  const { siteConfig, features, mission, gallery } = useContent();
  const doctor = useFeaturedDoctor();
  const facilityImage = gallery[2]?.url || gallery[0]?.url;
  return (
    <Page testid="about-page">
      <PageHero
        overline="About Us"
        titleLines={["Precision care,", <span key="2">rooted in <span className="italic text-alch-terracotta">warmth</span></span>]}
        subtitle="A dedicated centre for modern, minimally invasive surgical care in the Kumaon region — trusted since 2019."
      />

      <section className="px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <RevealImage src={facilityImage} alt="ALCH facility" className="h-[26rem] rounded-[2rem]" />
          </Reveal>
          <div>
            <Overline>Our Story</Overline>
            <Reveal>
              <h2 className="mt-6 font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
                Welcome to Advanced Laser Care Hospital
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 leading-relaxed text-alch-ink/80">
                Since {siteConfig.since}, we have focused on delivering precise, patient-friendly treatments using
                advanced laser and laparoscopic technology. Under the leadership of renowned surgeon
                Dr. Pramod Kumar Karan, we are widely trusted for the best piles and varicose vein treatment
                in Haldwani, and advanced day-care laser procedures.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 leading-relaxed text-alch-ink/80">
                We are proud to be among the first and most advanced laser treatment centres in the Kumaon region.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-alch-surface px-5 py-24 md:px-8 md:py-28">
        <div className="mx-auto max-w-7xl">
          <Overline>What sets us apart</Overline>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <Reveal key={f.no || f.title} delay={i * 0.08}>
                <div className="h-full rounded-[1.5rem] border border-alch-forest/10 bg-alch-bg p-8">
                  <span className="font-serif text-3xl text-alch-terracotta">{f.no || `0${i + 1}`}</span>
                  <h3 className="mt-5 font-serif text-2xl leading-snug text-alch-forest">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-alch-ink/70">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Overline>Our Mission</Overline>
          <div className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {mission.map((m, i) => (
              <Reveal key={m.no || m.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-6 border-t border-alch-forest/12 pt-8">
                  <span className="font-serif text-5xl leading-none text-alch-terracotta">{m.no || `0${i + 1}`}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-alch-forest">{m.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-alch-ink/70">{m.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 rounded-[2rem] bg-alch-forest p-10 text-alch-bg md:p-14">
            <p className="font-serif text-2xl italic leading-relaxed md:text-3xl">&ldquo;{doctor.quote}&rdquo;</p>
            <p className="mt-4 text-sm uppercase tracking-[0.16em] text-alch-terracotta">— {doctor.name}</p>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </Page>
  );
}
